// src/pages/Admin/AdminClientList.js
import React, { useEffect, useState } from "react";
import "../../styles/AdminClients.css";
import toast from "react-hot-toast";
import { FiEdit2, FiEye, FiPlus, FiTrash2 } from "react-icons/fi";

import {
  fetchAdminClients,
  fetchAdminUsers,
  createAdminClient,
  adminImpersonate,
  adminDeleteClient,
  adminUpdateClient,
  updateAdminUserOpsPermissions,
  fetchAdminClientApprovers,
  createAdminClientApprover,
  updateAdminClientApprover,
  deleteAdminClientApprover,
  DEFAULT_CLIENT_FEATURES,
} from "../../lib/api";
import { CLIENT_PAGE_FEATURE_FIELDS } from "../../lib/clientFeatures";

export default function AdminClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [savingOpsFor, setSavingOpsFor] = useState("");

  const [username, setUsername] = useState("");
  const [clientFolder, setClientFolder] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const [deletingClient, setDeletingClient] = useState("");

  const [editingClient, setEditingClient] = useState(null);
  const [editUsername, setEditUsername] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editFolder, setEditFolder] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editFeatures, setEditFeatures] = useState({ ...DEFAULT_CLIENT_FEATURES });
  const [savingEdit, setSavingEdit] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [approvalModules, setApprovalModules] = useState({});
  const [approversLoading, setApproversLoading] = useState(false);
  const [savingApprover, setSavingApprover] = useState(false);
  const [newApprover, setNewApprover] = useState({ username: "", display_name: "", password: "", approval_modules: [] });

  const loadClients = async () => {
    try {
      setLoading(true);
      const [data, users] = await Promise.all([fetchAdminClients(), fetchAdminUsers()]);
      setClients(Array.isArray(data) ? data : data?.clients || []);
      setAdminUsers((Array.isArray(users) ? users : []).filter((u) => String(u?.role || "") === "admin"));
    } catch (err) {
      setClients([]);
      setAdminUsers([]);
      toast.error(err?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const toggleOpsPerm = async (username, currentPerms, perm) => {
    const key = String(username || "");
    if (!key) return;
    const cur = new Set((currentPerms || []).map((x) => String(x || "").trim().toLowerCase()));
    if (cur.has(perm)) cur.delete(perm);
    else cur.add(perm);
    try {
      setSavingOpsFor(key);
      await updateAdminUserOpsPermissions(key, Array.from(cur));
      toast.success(`Updated ops permissions for ${key}`);
      await loadClients();
    } catch (err) {
      toast.error(err?.message || "Failed to update ops permissions");
    } finally {
      setSavingOpsFor("");
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleCreateClient = async (e) => {
    e.preventDefault();

    if (!username || !clientFolder || !password) {
      toast.error("All fields are required");
      return;
    }

    try {
      toast.loading("Creating client...", { id: "create" });
      await createAdminClient(username, password, clientFolder, displayName);
      toast.success("Client created!", { id: "create" });

      setUsername("");
      setPassword("");
      setClientFolder("");
      setDisplayName("");

      loadClients();
    } catch (err) {
      toast.error(err?.message || "Failed to create client", { id: "create" });
    }
  };

  const handleImpersonate = async (folder) => {
    try {
      toast.loading(`Switching to ${folder}...`, { id: "imp" });
      await adminImpersonate(folder);
      toast.success(`Switched to ${folder}`, { id: "imp" });
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err?.message || "Failed to switch client", { id: "imp" });
    }
  };

  const handleDeleteClient = async (folder) => {
    if (!folder) return;

    const ok = window.confirm(
      `Delete client "${folder}"?\n\nThis will remove the client login from users.json and delete the client's folder (uploads + processed data). This cannot be undone.`
    );
    if (!ok) return;

    try {
      setDeletingClient(folder);
      toast.loading(`Deleting ${folder}...`, { id: "del" });

      await adminDeleteClient(folder);

      toast.success("Client deleted", { id: "del" });
      await loadClients();
    } catch (err) {
      toast.error(err?.message || "Failed to delete client", { id: "del" });
    } finally {
      setDeletingClient("");
    }
  };

  const loadApprovers = async (clientName) => {
    if (!clientName) return;
    try {
      setApproversLoading(true);
      const data = await fetchAdminClientApprovers(clientName);
      setApprovers(Array.isArray(data?.approvers) ? data.approvers : []);
      setApprovalModules(data?.available_modules && typeof data.available_modules === "object" ? data.available_modules : {});
    } catch (err) {
      setApprovers([]);
      setApprovalModules({});
      toast.error(err?.message || "Failed to load client approvers");
    } finally {
      setApproversLoading(false);
    }
  };

  const openEdit = async (client) => {
    setEditingClient(client);
    setEditUsername(client?.username || "");
    setEditDisplayName(client?.display_name || "");
    setEditFolder(client?.client || "");
    setEditPassword("");
    setEditFeatures({ ...DEFAULT_CLIENT_FEATURES, ...(client?.features || {}) });
    setNewApprover({ username: "", display_name: "", password: "", approval_modules: [] });
    await loadApprovers(client?.client || "");
  };

  const closeEdit = () => {
    setEditingClient(null);
    setEditUsername("");
    setEditDisplayName("");
    setEditFolder("");
    setEditPassword("");
    setEditFeatures({ ...DEFAULT_CLIENT_FEATURES });
    setApprovers([]);
    setApprovalModules({});
    setNewApprover({ username: "", display_name: "", password: "", approval_modules: [] });
    setSavingEdit(false);
  };

  const toggleNewApproverModule = (moduleKey) => {
    setNewApprover((prev) => {
      const selected = new Set(prev.approval_modules || []);
      if (selected.has(moduleKey)) selected.delete(moduleKey);
      else selected.add(moduleKey);
      return { ...prev, approval_modules: Array.from(selected) };
    });
  };

  const handleCreateApprover = async (event) => {
    event.preventDefault();
    const clientName = editingClient?.client || "";
    if (!clientName) return;
    if (!newApprover.username.trim() || !newApprover.password || !(newApprover.approval_modules || []).length) {
      toast.error("Username, password and at least one approval module are required");
      return;
    }
    try {
      setSavingApprover(true);
      await createAdminClientApprover(clientName, newApprover);
      toast.success("Client approver created");
      setNewApprover({ username: "", display_name: "", password: "", approval_modules: [] });
      await loadApprovers(clientName);
    } catch (err) {
      toast.error(err?.message || "Failed to create client approver");
    } finally {
      setSavingApprover(false);
    }
  };

  const toggleApproverModule = async (approver, moduleKey) => {
    const clientName = editingClient?.client || "";
    if (!clientName || !approver?.username) return;
    const selected = new Set(approver.approval_modules || []);
    if (selected.has(moduleKey)) selected.delete(moduleKey);
    else selected.add(moduleKey);
    if (selected.size === 0) {
      toast.error("An approver must retain at least one module");
      return;
    }
    try {
      setSavingApprover(true);
      await updateAdminClientApprover(clientName, approver.username, { approval_modules: Array.from(selected) });
      await loadApprovers(clientName);
    } catch (err) {
      toast.error(err?.message || "Failed to update approval modules");
    } finally {
      setSavingApprover(false);
    }
  };

  const handleRemoveApprover = async (approver) => {
    const clientName = editingClient?.client || "";
    if (!clientName || !approver?.username) return;
    if (!window.confirm(`Remove approval access for ${approver.username}?`)) return;
    try {
      setSavingApprover(true);
      await deleteAdminClientApprover(clientName, approver.username);
      toast.success("Client approver removed");
      await loadApprovers(clientName);
    } catch (err) {
      toast.error(err?.message || "Failed to remove client approver");
    } finally {
      setSavingApprover(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!editingClient?.client) return;

    const payload = {};
    const trimmedUsername = editUsername.trim();
    const trimmedDisplayName = editDisplayName.trim();
    const trimmedFolder = editFolder.trim();
    const trimmedPassword = editPassword.trim();

    if (!trimmedUsername) {
      toast.error("Username cannot be empty");
      return;
    }
    if (!trimmedFolder) {
      toast.error("Client folder cannot be empty");
      return;
    }

    if (trimmedUsername !== (editingClient.username || "")) {
      payload.username = trimmedUsername;
    }
    if (trimmedDisplayName !== (editingClient.display_name || "")) {
      payload.display_name = trimmedDisplayName;
    }
    if (trimmedFolder !== (editingClient.client || "")) {
      payload.client_folder = trimmedFolder;
    }
    if (trimmedPassword) {
      payload.new_password = trimmedPassword;
    }
    const currentFeatures = { ...DEFAULT_CLIENT_FEATURES, ...(editingClient?.features || {}) };
    const nextFeatures = { ...DEFAULT_CLIENT_FEATURES, ...(editFeatures || {}) };
    const featuresChanged = CLIENT_PAGE_FEATURE_FIELDS.some((f) => Boolean(currentFeatures[f.key]) !== Boolean(nextFeatures[f.key]));
    if (featuresChanged) {
      payload.features = nextFeatures;
    }

    if (Object.keys(payload).length === 0) {
      toast("No changes to save");
      return;
    }

    try {
      setSavingEdit(true);
      toast.loading("Updating client...", { id: "edit" });
      await adminUpdateClient(editingClient.client, payload);
      toast.success("Client updated", { id: "edit" });
      await loadClients();
      closeEdit();
    } catch (err) {
      toast.error(err?.message || "Failed to update client", { id: "edit" });
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="admin-clients-page">
      <h1>Manage Clients</h1>

      <div className="client-form-box">
        <h2>Create New Client</h2>

        <form onSubmit={handleCreateClient}>
          <input
            type="text"
            placeholder="Client username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />

          <input
            type="text"
            placeholder="Client folder name (no spaces)"
            value={clientFolder}
            onChange={(e) => setClientFolder(e.target.value)}
          />

          <input
            type="password"
            placeholder="Client password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <input
            type="text"
            placeholder="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <div className="client-form-actions">
            <button type="submit" className="btn btn-primary">
              <FiPlus /> Create client
            </button>
          </div>
        </form>
      </div>

      {editingClient ? (
        <div className="client-form-box">
          <h2>Edit Client</h2>

          <form onSubmit={handleUpdateClient}>
            <input
              type="text"
              placeholder="Client username"
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
            />

            <input
              type="text"
              placeholder="Display name"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
            />

            <input
              type="text"
              placeholder="Client folder name (no spaces)"
              value={editFolder}
              onChange={(e) => setEditFolder(e.target.value)}
            />

            <input
              type="password"
              placeholder="New password (optional)"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
            />

            <div className="client-sections-title">Client Dashboard Sections</div>
            <div className="client-feature-checklist">
              {CLIENT_PAGE_FEATURE_FIELDS.map((f) => (
                <label key={f.key} className="client-feature-item">
                  <input
                    type="checkbox"
                    checked={Boolean(editFeatures?.[f.key])}
                    onChange={(e) =>
                      setEditFeatures((prev) => ({
                        ...(prev || {}),
                        [f.key]: Boolean(e.target.checked),
                      }))
                    }
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>

            <section className="client-approvers-section">
              <div>
                <div className="client-sections-title">Client Approvers</div>
                <p className="client-approvers-help">
                  Give selected client staff the authority to approve specific EMS modules. They retain client access, cannot approve their own changes, and each decision remains in the audit trail.
                </p>
              </div>

              <div className="client-approver-form">
                <input
                  type="text"
                  placeholder="Approver username"
                  value={newApprover.username}
                  onChange={(e) => setNewApprover((prev) => ({ ...prev, username: e.target.value }))}
                  disabled={savingApprover}
                />
                <input
                  type="text"
                  placeholder="Display name (optional)"
                  value={newApprover.display_name}
                  onChange={(e) => setNewApprover((prev) => ({ ...prev, display_name: e.target.value }))}
                  disabled={savingApprover}
                />
                <input
                  type="password"
                  placeholder="Temporary password"
                  value={newApprover.password}
                  onChange={(e) => setNewApprover((prev) => ({ ...prev, password: e.target.value }))}
                  disabled={savingApprover}
                />
                <div className="client-approver-modules">
                  {Object.entries(approvalModules).map(([moduleKey, label]) => (
                    <label key={moduleKey} className="client-feature-item">
                      <input
                        type="checkbox"
                        checked={(newApprover.approval_modules || []).includes(moduleKey)}
                        onChange={() => toggleNewApproverModule(moduleKey)}
                        disabled={savingApprover}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <button type="button" className="admin-btn" onClick={handleCreateApprover} disabled={savingApprover || approversLoading}>
                  <FiPlus /> {savingApprover ? "Saving..." : "Add client approver"}
                </button>
              </div>

              <div className="client-approver-list">
                {approversLoading ? <p>Loading approvers...</p> : null}
                {!approversLoading && approvers.length === 0 ? <p>No client approvers assigned yet.</p> : null}
                {approvers.map((approver) => (
                  <div className="client-approver-row" key={approver.username}>
                    <div>
                      <strong>{approver.display_name || approver.username}</strong>
                      {approver.display_name ? <span>{approver.username}</span> : null}
                    </div>
                    <div className="client-approver-modules compact">
                      {Object.entries(approvalModules).map(([moduleKey, label]) => (
                        <label key={moduleKey} className="client-feature-item">
                          <input
                            type="checkbox"
                            checked={(approver.approval_modules || []).includes(moduleKey)}
                            onChange={() => toggleApproverModule(approver, moduleKey)}
                            disabled={savingApprover}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                    <button type="button" className="admin-btn danger" onClick={() => handleRemoveApprover(approver)} disabled={savingApprover}>
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <div className="client-form-actions">
              <button type="submit" className="btn btn-primary" disabled={savingEdit}>
                <FiEdit2 /> {savingEdit ? "Saving..." : "Save changes"}
              </button>

              <button
                type="button"
                className="admin-btn secondary"
                onClick={closeEdit}
                disabled={savingEdit}
              >
                Cancel edit
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <h2 style={{ marginTop: 40 }}>All Clients</h2>

      <div className="table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Client Username</th>
              <th>Display Name</th>
              <th>Folder</th>
              <th>Package</th>
              <th>Total CO2e</th>
              <th>Last Upload</th>
              <th style={{ width: 340 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 14, opacity: 0.8 }}>
                  Loading clients...
                </td>
              </tr>
            ) : clients.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 14, opacity: 0.8 }}>
                  No clients found yet.
                </td>
              </tr>
            ) : (
              clients.map((c, idx) => {
                const folder = c.client || "";
                const isDeleting = deletingClient === folder;

                return (
                  <tr key={folder || idx}>
                    <td>{c.username || "-"}</td>
                    <td>{c.display_name || "-"}</td>
                    <td>{folder || "-"}</td>
                    <td>
                      {[
                        c?.features?.carbon_reporting ? "Carbon" : null,
                        c?.features?.iso_registers ? "ISO" : null,
                      ]
                        .filter(Boolean)
                        .join(" + ") || "-"}
                    </td>
                    <td>{Number(c.total_kgco2e || 0).toLocaleString()}</td>
                    <td>{c.last_upload || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          className="admin-btn secondary"
                          onClick={() => openEdit(c)}
                          disabled={!folder || isDeleting}
                          title="Edit this client"
                        >
                          <FiEdit2 /> Edit
                        </button>

                        <button
                          className="admin-btn"
                          onClick={() => handleImpersonate(folder)}
                          disabled={!folder || isDeleting}
                          title="Log in as this client"
                        >
                          <FiEye /> View as Client
                        </button>

                        <button
                          className="admin-btn danger"
                          onClick={() => handleDeleteClient(folder)}
                          disabled={!folder || isDeleting}
                          title="Delete this client"
                        >
                          <FiTrash2 /> {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <h2 style={{ marginTop: 32 }}>Admin Ops Permissions</h2>
      <div className="table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>ops_view</th>
              <th>ops_triage</th>
              <th>ops_admin</th>
            </tr>
          </thead>
          <tbody>
            {adminUsers.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 14, opacity: 0.8 }}>No admin users found.</td>
              </tr>
            ) : (
              adminUsers.map((u) => {
                const perms = (u?.ops_permissions || []).map((x) => String(x || "").trim().toLowerCase());
                const disabled = savingOpsFor === String(u?.username || "");
                return (
                  <tr key={String(u?.token || u?.username || Math.random())}>
                    <td>{u?.username || "-"}</td>
                    {["ops_view", "ops_triage", "ops_admin"].map((perm) => (
                      <td key={perm}>
                        <input
                          type="checkbox"
                          checked={perms.includes(perm)}
                          disabled={disabled}
                          onChange={() => toggleOpsPerm(u?.username, perms, perm)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
